/**
 * EmailNotificationSettingsCard - Gestion des destinataires de notification
 * Extrait de Profile.js pour améliorer la maintenabilité
 * AMIRAUX UNIQUEMENT
 */
import React from 'react';
import { Mail, AlertTriangle, ChevronDown, ChevronRight, MessageSquare, Globe, User, Plus, X, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * Section d'emails pour un type de notification
 */
const EmailSection = ({
  title,
  icon: Icon,
  description,
  emails,
  newEmail,
  onNewEmailChange,
  onAddEmail,
  onRemoveEmail,
  onSave,
  saving,
  testIdPrefix
}) => (
  <div className="p-4 bg-slate-900/50 rounded-lg border border-orange-800/40">
    <h4 className="text-orange-300 font-semibold mb-3 flex items-center gap-2">
      <Icon className="w-4 h-4" />
      {title}
    </h4>
    <p className="text-xs text-slate-500 mb-3">{description}</p>
    
    {/* Liste des emails */}
    <div className="space-y-2 mb-3">
      {(emails || []).map((email, idx) => (
        <div key={idx} className="flex items-center gap-2 bg-slate-800/60 rounded px-3 py-2">
          <span className="flex-1 text-sm text-slate-200 truncate">{email}</span>
          <button
            type="button"
            onClick={() => onRemoveEmail(email)}
            className="text-red-400 hover:text-red-300 p-1"
            data-testid={`remove-notif-${testIdPrefix}-${idx}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      {(emails || []).length === 0 && (
        <p className="text-xs text-amber-400 italic">Aucun destinataire configuré — les notifications ne seront pas envoyées.</p>
      )}
    </div>
    
    {/* Ajouter un email */}
    <div className="flex gap-2 mb-3">
      <Input
        type="email"
        value={newEmail || ''}
        onChange={(e) => onNewEmailChange(e.target.value)}
        placeholder="nouvelle@adresse.com"
        className="flex-1 bg-slate-950/50 border-slate-700 text-slate-100 text-sm"
        data-testid={`input-new-notif-${testIdPrefix}`}
        onKeyDown={(e) => e.key === 'Enter' && onAddEmail()}
      />
      <Button
        type="button"
        onClick={onAddEmail}
        size="sm"
        variant="outline"
        className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
        data-testid={`btn-add-notif-${testIdPrefix}`}
      >
        <Plus className="w-4 h-4" />
      </Button>
    </div>
    
    <Button
      onClick={onSave}
      disabled={saving}
      size="sm"
      className="bg-orange-600 hover:bg-orange-700 text-white"
      data-testid={`save-notif-${testIdPrefix}`}
    >
      <Save className="w-4 h-4 mr-2" />
      {saving ? 'Sauvegarde...' : 'Enregistrer'}
    </Button>
  </div>
);

export const EmailNotificationSettingsCard = ({
  showEmailNotifSettings,
  setShowEmailNotifSettings,
  emailNotifSettings,
  editingNotifEmails,
  newNotifEmail,
  setNewNotifEmail,
  savingNotifSettings,
  onAddNotifEmail,
  onRemoveNotifEmail,
  onSaveNotifEmails
}) => (
  <Card className="relative bg-slate-950/40 backdrop-blur-xl border-orange-800/60">
    <CardHeader
      className="cursor-pointer select-none hover:bg-slate-900/30 transition-colors rounded-t-lg"
      onClick={() => setShowEmailNotifSettings(!showEmailNotifSettings)}
      data-testid="toggle-email-notif-settings"
    >
      <CardTitle className="text-xl md:text-2xl font-orbitron uppercase tracking-wider text-orange-400 flex items-center justify-between">
        <span className="flex items-center">
          <Mail className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3" />
          Destinataires de notification
          <span className="ml-2 text-[10px] bg-orange-500/20 px-2 py-0.5 rounded border border-orange-500/30">AMIRAL</span>
        </span>
        {showEmailNotifSettings ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
      </CardTitle>
    </CardHeader>
    {showEmailNotifSettings && (
      <CardContent>
        <div className="text-sm text-slate-500 mb-4 space-y-1">
          <p className="text-orange-400 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <strong>Gestion des notifications Auto Edito</strong>
          </p>
          <p>Configurez les adresses qui reçoivent les alertes et notifications des différents services.</p>
        </div>

        {/* Boîtes mail actives */}
        {emailNotifSettings?.inboxes && emailNotifSettings.inboxes.length > 0 && (
          <div className="mb-6 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
            <p className="text-xs text-slate-400 mb-2">Boîtes mail actives :</p>
            <div className="flex flex-wrap gap-2">
              {emailNotifSettings.inboxes.map(inbox => (
                <span key={inbox.email} className={`text-xs px-2 py-1 rounded ${inbox.enabled ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-slate-700/50 text-slate-500 border border-slate-600'}`}>
                  <Mail className="w-3 h-3 inline mr-1" />
                  {inbox.email} ({inbox.type})
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Section Interviews */}
          <EmailSection
            title="Interviews — Notifications internes"
            icon={MessageSquare}
            description='Ces adresses reçoivent les alertes "[À VALIDER]", "Arbitrage requis", "Suivi manuel", etc.'
            emails={editingNotifEmails.interview_notification}
            newEmail={newNotifEmail.interview_notification}
            onNewEmailChange={(val) => setNewNotifEmail(prev => ({ ...prev, interview_notification: val }))}
            onAddEmail={() => onAddNotifEmail('interview_notification')}
            onRemoveEmail={(email) => onRemoveNotifEmail('interview_notification', email)}
            onSave={() => onSaveNotifEmails('interview_notification')}
            saving={savingNotifSettings.interview_notification}
            testIdPrefix="interview"
          />

          {/* Section Partenaires - CC obligatoires */}
          <EmailSection
            title="Partenaires — CC obligatoires"
            icon={Globe}
            description="Ces adresses sont systématiquement mises en copie des échanges partenaires."
            emails={editingNotifEmails.partenaire_mandatory_cc}
            newEmail={newNotifEmail.partenaire_mandatory_cc}
            onNewEmailChange={(val) => setNewNotifEmail(prev => ({ ...prev, partenaire_mandatory_cc: val }))}
            onAddEmail={() => onAddNotifEmail('partenaire_mandatory_cc')}
            onRemoveEmail={(email) => onRemoveNotifEmail('partenaire_mandatory_cc', email)}
            onSave={() => onSaveNotifEmails('partenaire_mandatory_cc')}
            saving={savingNotifSettings.partenaire_mandatory_cc}
            testIdPrefix="partenaire-cc"
          />

          {/* Section Partenaires - Super users */}
          <EmailSection
            title="Partenaires — Super utilisateurs"
            icon={User}
            description="Ces utilisateurs ont des droits étendus sur le module partenaires (whitelist automatique)."
            emails={editingNotifEmails.partenaire_super_users}
            newEmail={newNotifEmail.partenaire_super_users}
            onNewEmailChange={(val) => setNewNotifEmail(prev => ({ ...prev, partenaire_super_users: val }))}
            onAddEmail={() => onAddNotifEmail('partenaire_super_users')}
            onRemoveEmail={(email) => onRemoveNotifEmail('partenaire_super_users', email)}
            onSave={() => onSaveNotifEmails('partenaire_super_users')}
            saving={savingNotifSettings.partenaire_super_users}
            testIdPrefix="partenaire-super"
          />
        </div>
      </CardContent>
    )}
  </Card>
);

export default EmailNotificationSettingsCard;
